/**
 * Connection Type Selector Component
 *
 * Allows room organizers to select which connection types are allowed in the room.
 * Default is "All" (all transports enabled). Can limit to specific types like
 * WiFi only, Bluetooth only, etc.
 */

import { useState, useCallback } from 'react'
import {
  type AllowedTransportsConfig,
  type AllowedTransport,
  DEFAULT_ALLOWED_TRANSPORTS,
  TRANSPORT_OPTIONS,
} from '@/lib/types'

interface ConnectionTypeSelectorProps {
  value: AllowedTransportsConfig
  onChange: (config: AllowedTransportsConfig) => void
  compact?: boolean
}

export function ConnectionTypeSelector({
  value,
  onChange,
  compact = false,
}: ConnectionTypeSelectorProps) {
  // Check if all transports are enabled (default "All" state)
  const allEnabled = value.wifi && value.bluetooth && value.cellular && value.lora

  // Toggle "All" mode - either enable all or show individual selection
  const [showIndividual, setShowIndividual] = useState(!allEnabled)

  const handleAllToggle = useCallback(() => {
    if (allEnabled && !showIndividual) {
      // Switch from "All" to individual selection mode
      setShowIndividual(true)
    } else {
      // Enable all and hide individual selection
      onChange(DEFAULT_ALLOWED_TRANSPORTS)
      setShowIndividual(false)
    }
  }, [allEnabled, showIndividual, onChange])

  const handleTransportToggle = useCallback(
    (transport: AllowedTransport) => {
      const newConfig = { ...value, [transport]: !value[transport] }

      // Ensure at least one transport is enabled
      const enabledCount = Object.values(newConfig).filter(Boolean).length
      if (enabledCount === 0) {
        return // Don't allow disabling all transports
      }

      onChange(newConfig)
    },
    [value, onChange]
  )

  if (compact) {
    return (
      <div className="flex flex-col gap-1.5">
        {/* All toggle */}
        <button
          type="button"
          onClick={handleAllToggle}
          className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            allEnabled && !showIndividual
              ? 'bg-primary text-white'
              : 'bg-white/10 text-white/70 hover:bg-white/20'
          }`}
        >
          <span>All Connections</span>
          {allEnabled && !showIndividual && <span className="text-white/80">Default</span>}
        </button>

        {/* Individual transport toggles */}
        {showIndividual && (
          <div className="flex flex-wrap gap-1">
            {TRANSPORT_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => handleTransportToggle(option.key)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                  value[option.key]
                    ? 'bg-primary/80 text-white'
                    : 'bg-white/5 text-white/40 hover:bg-white/10'
                }`}
              >
                <span>{option.icon}</span>
                <span>{option.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Header with All toggle */}
      <div className="flex items-center justify-between">
        <label className="text-sm text-white/60">Connection Types</label>
        <button
          type="button"
          onClick={handleAllToggle}
          className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
            allEnabled && !showIndividual
              ? 'bg-primary text-white'
              : 'bg-white/10 text-white/70 hover:bg-white/20'
          }`}
        >
          <span>All</span>
          {allEnabled && !showIndividual && (
            <span className="text-[10px] text-white/70">(Default)</span>
          )}
        </button>
      </div>

      {/* Individual transport options */}
      {showIndividual && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {TRANSPORT_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => handleTransportToggle(option.key)}
              className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition-all ${
                value[option.key]
                  ? 'bg-primary/20 border-primary text-white'
                  : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'
              }`}
            >
              <span className="text-xl">{option.icon}</span>
              <span className="text-xs font-medium">{option.label}</span>
              <span className="text-[10px] text-white/50">{option.range}</span>
            </button>
          ))}
        </div>
      )}

      {/* Warning if limited connections */}
      {showIndividual && !allEnabled && (
        <p className="text-[10px] text-yellow-400/80 mt-1">
          Limited connections may reduce mesh reliability
        </p>
      )}
    </div>
  )
}
